import React, { useState } from 'react';
import { Settings2 } from 'lucide-react';
import type { SiteEntity } from '../../../types/site';
import { Button } from '../../ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../ui/dialog';
import { StatusPill } from '../../ui/status-pill';
import { SiteConfigurationPanel } from '../SiteConfigurationPanel';

export interface ReportPreferencesDialogProps {
  site: SiteEntity;
  customizedCount?: number;
  onJumpToEvidence?: () => void;
  triggerMode?: 'button' | 'icon' | 'hidden';
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  lockReadOnly?: boolean;
  targetReportType?: import('../../../config/reportTypes').ReportTypeId;
}

export const ReportPreferencesDialog: React.FC<ReportPreferencesDialogProps> = ({
  site,
  customizedCount = 0,
  onJumpToEvidence,
  triggerMode = 'button',
  targetReportType,
  open: controlledOpen,
  onOpenChange,
  lockReadOnly,
}) => {
  const [localOpen, setLocalOpen] = useState(false);
  const open = controlledOpen ?? localOpen;
  const setOpen = (next: boolean) => { setLocalOpen(next); onOpenChange?.(next); };

  return (
    <>
      {/* Aurora cockpit chrome (founder live-QA round 2, item 3): the old
          light outline chrome → the `mission` variant (surface fill,
          hairline border, uppercase eyebrow tracking) so the trigger
          matches the redesigned cockpit secondary actions. */}
      {triggerMode === 'hidden' ? null : triggerMode === 'icon' ? (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-text-muted hover:text-text-primary"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          title={`Report Variables${targetReportType ? ` for ${targetReportType}` : ''}`}
          aria-label={`Report Variables${targetReportType ? ` for ${targetReportType}` : ''}`}
          data-testid="reports-preferences-open-icon"
        >
          <Settings2 className="h-4 w-4" aria-hidden />
          {customizedCount > 0 && (
            <span className="absolute top-1 right-1 w-2 h-2 rounded-full bg-accent" aria-hidden />
          )}
        </Button>
      ) : (
        <Button
          type="button"
          variant="mission"
          size="sm"
          className="h-8 gap-1.5 shrink-0"
          onClick={(e) => { e.stopPropagation(); setOpen(true); }}
          data-testid="reports-preferences-open"
        >
          <Settings2 className="h-3.5 w-3.5" aria-hidden />
          Report Variables
          {customizedCount > 0 ? (
            <StatusPill variant="custom" className="ml-0.5">{customizedCount}</StatusPill>
          ) : (
            <StatusPill variant="using-defaults" className="ml-0.5 hidden sm:inline-flex">Defaults</StatusPill>
          )}
        </Button>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent size="xl" className="max-h-[min(85vh,900px)] overflow-y-auto p-0 gap-0">
          <DialogHeader className="px-4 pt-4 pb-3 border-b border-border-subtle">
            <DialogTitle className="type-section text-text-primary">Report Variables</DialogTitle>
            <DialogDescription className="type-caption">
              Per-report-type settings — customize flavour before generating or rerunning.
            </DialogDescription>
          </DialogHeader>
          <div className="p-4" data-testid="reports-prefs-dialog-body">
            <SiteConfigurationPanel
              site={site}
              lockReadOnly={lockReadOnly}
              testIdPrefix="reports-prefs-config"
              visibleSections={['prefs']}
              hideShell
              flattenSections
              defaultExpandedSection="prefs"
              onJumpToEvidenceTab={onJumpToEvidence}
              filterReportTypes={targetReportType ? [targetReportType] : undefined}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};
